import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { categories } from '../../data/conditions';
import { AssigneeSelect } from './AssigneeSelect';
import { InlineDateField } from './InlineDateField';
import { InlineTextField } from './InlineTextField';
import { RowActions } from './RowActions';
import { StatusSelect } from './StatusSelect';
import { conditionRowGrid, daysOpen, daysPastEta, etaApplies, formatDate, formatShortDate, getEscalation, isResolved } from '../../utils/conditions';
import type { ConditionPatch } from '../../hooks/useConditions';
import type { Condition } from '../../types/conditions';
interface ConditionRowProps {
  condition: Condition;
  selected: boolean;
  onToggleSelect: () => void;
  onUpdate: (patch: ConditionPatch) => void;
  onComments: () => void;
  onUpload: (fileName: string) => void;
  onLinkDocuments: () => void;
  showCategory?: boolean;
}
export function ConditionRow({
  condition,
  selected,
  onToggleSelect,
  onUpdate,
  onComments,
  onUpload,
  onLinkDocuments,
  showCategory = false
}: ConditionRowProps) {
  const escalation = getEscalation(condition);
  const resolved = isResolved(condition.status);
  const overdue = daysPastEta(condition);
  const name = condition.name;
  return <motion.div layout initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18
  }} role="row" className={`${conditionRowGrid} border-b border-slate-100 px-6 py-3.5 transition-colors ${selected ? 'bg-brand/[0.04]' : 'hover:bg-slate-50/70'}`} data-id="element-123" data-mp="2ogukoq0e5u0">
      <div role="cell" data-id="element-124" data-mp="gmx6qv6n4ojj">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} aria-label={`Select ${name}`} className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" data-id="element-125" data-mp="lnus1gmy5yp9" />
      </div>

      <div role="cell" className="min-w-0" data-id="element-126" data-mp="1e6004nr2akvq">
        <InlineTextField value={condition.name} onSave={v => v && onUpdate({
        name: v
      })} ariaLabel="condition name" textClassName={`text-sm font-medium ${resolved ? 'text-slate-500' : 'text-slate-900'}`} data-id="element-127" data-mp="1o05mtuuga6yd" />
        <InlineTextField value={condition.description} onSave={v => onUpdate({
        description: v
      })} ariaLabel={`description for ${name}`} placeholder="Add description…" textClassName="text-sm text-slate-500" data-id="element-128" data-mp="1e0epzmif3fv4" />
        {showCategory && <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-129" data-mp="1098h4zt905vk">
            {categories.find(cat => cat.id === condition.category)?.label}
          </span>}
        {escalation !== 'none' && <p className="mt-0.5 text-xs text-red-600" data-id="element-130" data-mp="m9qt4mns0wn9">
            No ETA · {daysOpen(condition)}d ·{' '}
            {escalation === 'avp' ? 'escalated to AVP of Processing' : 'processor emailed'}
          </p>}
      </div>

      <div role="cell" data-id="element-131" data-mp="2t1nb6zi9n2ud">
        <StatusSelect value={condition.status} onChange={status => onUpdate({
        status
      })} conditionName={name} data-id="element-132" data-mp="2xy5d7rm7on30" />
      </div>

      <div role="cell" className="text-sm tabular-nums text-slate-700" title={formatDate(condition.createdAt)} data-id="element-133" data-mp="321203uisb5cr">
        {formatShortDate(condition.createdAt)}
      </div>

      <div role="cell" data-id="element-134" data-mp="2a92yariyp8c7">
        <InlineDateField value={condition.requestedAt} onSave={v => onUpdate({
        requestedAt: v
      })} ariaLabel={`Requested date for ${name}`} emptyLabel="Set date" data-id="element-135" data-mp="2a9uil673zjn6" />
      </div>

      <div role="cell" data-id="element-136" data-mp="2j7y0lsivxya8">
        {resolved ? <InlineDateField value={condition.clearedAt} onSave={v => onUpdate({
        clearedAt: v
      })} ariaLabel={`Cleared date for ${name}`} emptyLabel="Set date" tone="success" data-id="element-137" data-mp="2nzy0xm2vat7c" /> : !etaApplies(condition.status) ? <span className="inline-flex items-center gap-1 text-sm text-steel" title={condition.eta ? `ETA was ${formatDate(condition.eta)}` : 'Documents received'} data-id="element-138" data-mp="20y3ulssq1sd">
            <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-139" data-mp="at9hf72t9uqx" />
            Met
          </span> : <div data-id="element-140" data-mp="629n89l2tqs4">
            <InlineDateField value={condition.eta} onSave={v => onUpdate({
          eta: v
        })} ariaLabel={overdue > 0 ? `ETA for ${name}, ${overdue} days overdue` : `ETA for ${name}`} emptyLabel="Set ETA" tone={overdue > 0 ? 'danger' : 'default'} data-id="element-141" data-mp="61qb59i3xwm0" />
            {overdue > 0 && <span className="block text-xs text-red-600" data-id="element-142" data-mp="pqseqxtk1g9z">
                {overdue} {overdue === 1 ? 'day' : 'days'} overdue
              </span>}
          </div>}
      </div>

      <div role="cell" data-id="element-143" data-mp="2ri56el6ihro3">
        <AssigneeSelect value={condition.assigneeId} onChange={assigneeId => onUpdate({
        assigneeId
      })} conditionName={name} data-id="element-144" data-mp="2rin9a0noj44z" />
      </div>

      <div role="cell" data-id="element-145" data-mp="2lfsevh0sv9mz">
        <RowActions condition={condition} onToggleEmail={() => onUpdate({
        includeInEmail: !condition.includeInEmail
      })} onToggleBorrowerVisible={() => onUpdate({
        borrowerVisible: !condition.borrowerVisible
      })} onComments={onComments} onUpload={onUpload} onLinkDocuments={onLinkDocuments} data-id="element-146" data-mp="2lg1vidzyg4g0" />
      </div>
    </motion.div>;
}